import { c as _c } from "react/compiler-runtime";
import { useCallback, useState } from 'react';
import type { ChartLegendItem } from '../figure/ChartLegend';
import type { TouchDesignSerie } from '../interfaces';
import { TouchDesignSerieVariant } from '../KeyboardChart';
const LEGEND_IDS: Record<TouchDesignSerieVariant, string> = {
  [TouchDesignSerieVariant.Computed]: 'computed',
  [TouchDesignSerieVariant.Default]: 'standard-soft',
  [TouchDesignSerieVariant.DefaultBold]: 'standard-bold',
  [TouchDesignSerieVariant.Measured]: 'measured',
  [TouchDesignSerieVariant.Target]: 'target'
};

/** The legend entry a curve belongs to, named after the way it is drawn. */
export const legendIdOf = (serie: TouchDesignSerie): string => LEGEND_IDS[serie.variant ?? TouchDesignSerieVariant.Measured];

/**
 * Which entries of a figure's legend the reader has switched off.
 *
 * ECharts' own legend hid a series on a click, and readers lean on it to see
 * the measurement without the standard curves crossing it, or the target
 * without the measurement. The figures draw their own legend now, so they
 * keep that here: every entry toggles, and `isShown` tells the figure what
 * to leave off the plot. The scale stays the one all the curves need, so
 * hiding a curve never moves the others.
 */
export const useLegendToggles = () => {
  const $ = _c(8);
  const [hidden, setHidden] = useState(_temp);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = id => {
      setHidden(current => {
        const next = new Set(current);
        if (!next.delete(id)) {
          next.add(id);
        }
        return next;
      });
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const toggle = t0;
  let t1;
  if ($[1] !== hidden) {
    t1 = id_0 => !hidden.has(id_0);
    $[1] = hidden;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isShown = t1;
  let t2;
  if ($[3] !== hidden) {
    t2 = items => items.map(item => ({
      ...item,
      isVisible: !hidden.has(item.id),
      onToggle: () => toggle(item.id)
    }));
    $[3] = hidden;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const withToggles = t2;
  let t3;
  if ($[5] !== isShown || $[6] !== withToggles) {
    t3 = {
      isShown,
      withToggles
    };
    $[5] = isShown;
    $[6] = withToggles;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
function _temp() {
  return new Set();
}